import { AspectRatio } from "@/services/bedrock/dtos/DesignPackage";
import type { DesignBasis } from "./NewDesignPage";

export interface TemplatePreset {
  teaser: string;
  basis: DesignBasis;
  format: "single" | "carousel";
  aspectRatio: AspectRatio | "auto";
  chipIds: string[];
}

/** Keyed by global package name; a renamed global falls back to its own structure with no chips. */
export const TEMPLATE_PRESETS: Record<string, TemplatePreset> = {
  "Editorial Spread": {
    teaser: "Warm, quiet, serif",
    basis: "review",
    format: "single",
    aspectRatio: "portrait_4_5",
    chipIds: ["outlined-box", "light-airy", "serif-quote", "profile-pill"],
  },
  "Split-Screen": {
    teaser: "Photo left, brand panel right",
    basis: "review",
    format: "single",
    aspectRatio: "square_1_1",
    chipIds: ["split", "home-photo", "friendly-sans", "stars", "verified-badge", "headshot-overlap"],
  },
  "Monochrome Card": {
    teaser: "Dark, one brand color",
    basis: "review",
    format: "single",
    aspectRatio: "square_1_1",
    chipIds: ["outlined-box", "brand-only", "dark-mode", "highlight-phrase", "profile-pill"],
  },
  "SMS Chat Bubble": {
    teaser: "Review as a text message",
    basis: "review",
    format: "single",
    aspectRatio: "portrait_9_16",
    chipIds: ["family-photo", "friendly-sans", "chat-bubble", "headshot-overlap"],
  },
  Glassmorphism: {
    teaser: "Frosted card over a home photo",
    basis: "review",
    format: "single",
    aspectRatio: "square_1_1",
    chipIds: ["glass", "home-photo", "friendly-sans", "stars", "headshot-overlap", "profile-pill"],
  },
  "Scrapbook Polaroid": {
    teaser: "Casual, high trust",
    basis: "review",
    format: "single",
    aspectRatio: "square_1_1",
    chipIds: ["polaroid", "family-photo", "friendly-sans", "branch-logo"],
  },
  "Brutalist Typography": {
    teaser: "Loud type, no photo",
    basis: "review",
    format: "single",
    aspectRatio: "square_1_1",
    chipIds: ["brand-only", "chunky", "highlight-phrase", "profile-pill"],
  },
  "Cut-Out Portrait": {
    teaser: "Headshot over the quote",
    basis: "review",
    format: "single",
    aspectRatio: "portrait_9_16",
    chipIds: ["home-photo", "friendly-sans", "headshot-overlap", "profile-pill"],
  },
  "Neon Glow": {
    teaser: "Black with a neon accent",
    basis: "review",
    format: "single",
    aspectRatio: "portrait_9_16",
    chipIds: ["brand-only", "dark-mode", "neon", "chunky", "profile-pill"],
  },
  "Dashboard Widget": {
    teaser: "Structured like an app",
    basis: "review",
    format: "single",
    aspectRatio: "portrait_4_5",
    chipIds: ["widget", "light-airy", "friendly-sans", "stars", "platform", "profile-pill"],
  },
  "Client Journey": {
    teaser: "Hook, review, call to action",
    basis: "review",
    format: "carousel",
    aspectRatio: "portrait_4_5",
    chipIds: ["intro-outro", "home-photo", "serif-quote", "highlight-phrase", "headshot-overlap"],
  },
  "Triple Proof": {
    teaser: "Three reviews, dark slate",
    basis: "review",
    format: "carousel",
    aspectRatio: "square_1_1",
    chipIds: ["home-photo", "dark-mode", "serif-quote", "stars", "verified-badge", "profile-pill"],
  },
  "Seamless Ribbon": {
    teaser: "A ribbon ties the slides together",
    basis: "review",
    format: "carousel",
    aspectRatio: "square_1_1",
    chipIds: ["ribbon", "intro-outro", "dark-mode", "chunky", "profile-pill"],
  },
  "Meet the Team": {
    teaser: "Player spotlight",
    basis: "other",
    format: "single",
    aspectRatio: "portrait_4_5",
    chipIds: [
      "about-player",
      "light-airy",
      "friendly-sans",
      "headshot-overlap",
      "profile-pill",
      "tagline",
      "branch-logo",
    ],
  },
};
